import DialogProvider from "@components/dialog_provider"
import { oneTimesUpdate } from "@domain/coupons"
import { HumanReplyLearningJob } from "@domain/scripts"
import { useCustomersQuery } from "@hooks/use_customers"
import useTranslate from "@hooks/use_translate"
import { Autocomplete, Stack, TextField, Typography } from "@mui/material"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { OneTimesType } from "__generated__/graphql"
import { useState } from "react"

const LAST_HUMAN_REPLY_LEARNING_CUSTOMER_ID_STORAGE_KEY =
  "scripts:human-reply-learning:last-customer-id"
const LAST_HUMAN_REPLY_LEARNING_JOB_ID_STORAGE_KEY =
  "scripts:human-reply-learning:last-job-id"

type HumanReplyLearningRunDialogProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  onStarted?: (params: { customerId: string; jobId: string }) => void
}

export default function HumanReplyLearningRunDialog({
  isOpen,
  setIsOpen,
  onStarted
}: HumanReplyLearningRunDialogProps) {
  const { translate } = useTranslate()
  const [customerId, setCustomerId] = useState("")
  const [conversationId, setConversationId] = useState("")
  const [loading, setLoading] = useState(false)

  const { customerOptions, isLoading: customersLoading } =
    useCustomersQuery(isOpen)

  function handleClose() {
    if (loading) return
    setIsOpen(false)
  }

  async function onSubmit() {
    if (!customerId.trim()) {
      showErrorToast("Customer is required")
      return
    }

    if (!conversationId.trim()) {
      showErrorToast("Conversation ID is required")
      return
    }

    setLoading(true)
    const response = await oneTimesUpdate({
      type: OneTimesType.HumanReplyLearning,
      metaData: { customerId, conversationId }
    })

    const rawJob = response.data?.data
    const startedJob = (Array.isArray(rawJob) ? rawJob[0] : rawJob) as
      | HumanReplyLearningJob
      | undefined

    if (!response.isSuccess || !startedJob?.jobId) {
      setLoading(false)
      showErrorToast(
        response.errorMessage ?? "Failed to start human reply learning"
      )
      return
    }

    localStorage.setItem(
      LAST_HUMAN_REPLY_LEARNING_CUSTOMER_ID_STORAGE_KEY,
      customerId
    )
    localStorage.setItem(
      LAST_HUMAN_REPLY_LEARNING_JOB_ID_STORAGE_KEY,
      startedJob.jobId
    )
    showSuccessToast("Human reply learning started in background")
    setLoading(false)
    setIsOpen(false)
    onStarted?.({
      customerId,
      jobId: startedJob.jobId
    })
  }

  return (
    <DialogProvider
      title="Run Human Reply Learning"
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: loading
      }}
      rightButton={{
        text: "Start",
        color: "primary",
        onClick: () => void onSubmit(),
        loading,
        disabled: loading || customersLoading
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={2}>
        <Typography variant="body2" color="text.secondary">
          Run learning for a single closed conversation.
        </Typography>
        <Autocomplete
          options={customerOptions}
          disabled={customersLoading || loading}
          value={customerOptions.find((o) => o.id === customerId) ?? null}
          onChange={(_, option) => setCustomerId(option?.id ?? "")}
          getOptionLabel={(o) => `${o.label} (${o.id})`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate("customer")}
              placeholder="Müşteri adı veya ID ile arayın…"
            />
          )}
          noOptionsText="Müşteri bulunamadı"
          loadingText="Yükleniyor…"
          loading={customersLoading}
        />
        <TextField
          fullWidth
          label="Conversation ID"
          value={conversationId}
          disabled={loading}
          onChange={(event) => setConversationId(event.target.value)}
        />
      </Stack>
    </DialogProvider>
  )
}
